<template>
  <div class="journey-preview">
    <div v-if="!hasJourney" class="jp-empty">
      No journey loaded yet.<br>Use the Chat tab to build one, then preview it here.
    </div>
    <template v-else>
      <div v-if="availableLanguages.length > 1" class="jp-lang-bar">
        <button
          v-for="lang in availableLanguages"
          :key="lang"
          :class="['jp-lang-btn', { 'jp-lang-btn--active': selectedLang === lang }]"
          @click="switchLang(lang)">
          {{ lang }}
        </button>
      </div>
      <div class="jp-messages" ref="messagesContainer">
        <div v-for="(msg, i) in messages" :key="i" :class="['jp-msg', msg.role]">
          <div class="jp-msg-inner">
            <div v-if="msg.text" class="jp-bubble" v-html="renderText(msg.text)" />
            <div v-if="msg.hint" class="jp-hint">{{ msg.hint }}</div>
            <div v-if="msg.stageName" class="jp-stage-label">{{ msg.stageName }}</div>
          </div>
        </div>
        <div v-if="ended" class="jp-ended">{{ endNote || 'Journey complete ✓' }}</div>
        <div v-if="stopReason" class="jp-stopped">{{ stopReason }}</div>
      </div>
      <div class="jp-actions">
        <template v-if="!ended && pending">
          <div v-if="pending.buttons && pending.buttons.length" class="jp-choice-buttons">
            <button
              v-for="(btn, i) in pending.buttons"
              :key="i"
              class="jp-choice-btn"
              @click="selectChoice(i)">
              {{ btn }}
            </button>
          </div>
          <div v-else-if="pending.isTextInput" class="jp-text-input-bar">
            <input
              v-model="textInputValue"
              class="jp-text-input"
              :placeholder="pending.inputHint || 'Type your response...'"
              @keydown.enter="submitTextInput"
            />
            <button class="jp-text-submit" @click="submitTextInput">→</button>
          </div>
        </template>
        <button v-if="messages.length > 0" class="jp-restart-btn" @click="restart">
          ↺ Restart
        </button>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'JourneyPreview',
  props: {
    currentJourneyData: {
      type: Object,
      default: () => ({})
    },
    // Only run while the Preview tab is showing. The tab is kept alive with v-show, so without this
    // the preview kept auto-playing in the background and restarted on every save.
    active: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      messages: [],
      pending: null,
      ended: false,
      selectedLang: 'eng',
      textInputValue: '',
      endNote: '',
      stopReason: '',
      // The journey changed while the tab was hidden; restart when it's shown again
      stale: false,
      runId: 0,
      timer: null,
      // Stages passed through on their own since the user last answered (loop check)
      autoVisited: []
    }
  },
  computed: {
    hasJourney () {
      return !!(this.currentJourneyData && this.currentJourneyData.config && this.currentJourneyData.config.stages)
    },
    stages () {
      return this.hasJourney ? this.currentJourneyData.config.stages : {}
    },
    availableLanguages () {
      if (!this.hasJourney) return []
      const langs = new Set()
      Object.values(this.stages).forEach(stage => {
        if (!stage.text) return
        stage.text.forEach(item => {
          if (item && typeof item === 'object') {
            Object.values(item).forEach(localeObj => {
              if (localeObj && typeof localeObj === 'object') {
                Object.keys(localeObj).forEach(l => langs.add(l))
              }
            })
          }
        })
      })
      return Array.from(langs)
    }
  },
  watch: {
    currentJourneyData () {
      if (!this.hasJourney) return
      if (this.active) {
        this.resetLanguage()
        this.restart()
      } else {
        this.stale = true
      }
    },
    active (isActive) {
      if (isActive && this.hasJourney && (this.stale || !this.messages.length)) {
        this.resetLanguage()
        this.restart()
      }
    }
  },
  mounted () {
    if (this.hasJourney && this.active) {
      this.resetLanguage()
      this.restart()
    }
  },
  beforeDestroy () {
    clearTimeout(this.timer)
  },
  methods: {
    // Reset lang to first available when journey changes
    resetLanguage () {
      const langs = this.availableLanguages
      this.selectedLang = langs.includes('eng') ? 'eng' : (langs[0] || 'eng')
    },
    switchLang (lang) {
      this.selectedLang = lang
      this.restart()
    },
    getRawText (textArray) {
      if (!textArray || !textArray.length) return ''
      // Each item in text[] is resolved independently, then concatenated.
      // This handles journeys that split display text and {{buttons}} across separate items.
      const parts = []
      for (const item of textArray) {
        if (typeof item === 'string') { parts.push(item); continue }
        if (!item || typeof item !== 'object') continue
        const localeObjects = Object.values(item).filter(v => v && typeof v === 'object')
        // Prefer selectedLang within this item
        let found = null
        for (const loc of localeObjects) {
          if (loc[this.selectedLang]) { found = loc[this.selectedLang]; break }
        }
        // Fallback to eng within this item
        if (!found) {
          for (const loc of localeObjects) {
            if (loc['eng']) { found = loc['eng']; break }
          }
        }
        // Last resort: first string value in this item
        if (!found) {
          for (const loc of localeObjects) {
            const first = Object.values(loc).find(v => typeof v === 'string')
            if (first) { found = first; break }
          }
        }
        if (found) parts.push(found)
      }
      return parts.join('\n')
    },
    parseText (textArray) {
      const raw = this.getRawText(textArray)
      const buttons = []
      const hints = []
      const displayText = raw
        .replace(/\{\{([^}]+)\}\}/g, (_, label) => { buttons.push(label); return '' })
        .replace(/\[\[([^\]]+)\]\]/g, (_, hint) => { hints.push(hint); return '' })
        .replace(/\n{3,}/g, '\n\n')
        .trim()
      return { displayText, buttons, hints }
    },
    renderText (text) {
      return text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>')
    },
    nextList (stage) {
      if (Array.isArray(stage.next)) return stage.next.filter(Boolean)
      return stage.next ? [stage.next] : []
    },
    // The stage for choice `index`. Editor stages can have more options than next stages (several
    // options sharing one route), so fall back to the first route.
    nextStageFor (stage, index) {
      const nexts = this.nextList(stage)
      return nexts[index] || nexts[0] || null
    },
    goTo (nextName) {
      if (!nextName || nextName === 'end') {
        this.ended = true
        this.pending = null
        this.scrollToBottom()
        return
      }
      this.enterStage(nextName)
    },
    enterStage (name) {
      const stage = this.stages[name]
      if (!stage || stage.nextApp || name === 'end') {
        const text = stage ? this.parseText(stage.text).displayText : ''
        if (text) this.messages.push({ role: 'bot', text, stageName: name })
        if (!stage) {
          this.endNote = `Stage "${name}" isn't set up yet, so the preview stops here.`
        } else if (stage.nextApp && stage.nextApp !== 'END_CONVERSATION') {
          this.endNote = `Hands over to ${stage.nextApp.split('###').pop()} ✓`
        }
        this.ended = true
        this.pending = null
        this.scrollToBottom()
        return
      }
      const { displayText, buttons, hints } = this.parseText(stage.text)
      const nexts = this.nextList(stage)
      const options = Array.isArray(stage.options) ? stage.options.filter(Boolean) : []
      // Choices, in order of preference: {{buttons}} in the text (Chat-built journeys), the stage's
      // options (editor-built journeys), or the possible next stages when something else picks the
      // route (e.g. a processing function).
      let choices = buttons.length ? buttons : options
      let hint = hints[0] || null
      if (!choices.length && nexts.length > 1) {
        choices = nexts
        const fn = stage.processingFunctionConfig && stage.processingFunctionConfig.functionName
        hint = hint || (fn ? `In the live bot, ${fn} picks the next stage. Pick one to continue.` : 'Pick the route to follow.')
      }
      const asksForText = stage.input === true || !!(stage.validations && stage.validations.type)
      if (displayText || hint) this.messages.push({ role: 'bot', text: displayText, hint, stageName: name })

      if (choices.length && (asksForText || options.length || buttons.length || nexts.length > 1)) {
        this.pending = { name, stage, buttons: choices }
      } else if (asksForText) {
        this.textInputValue = ''
        this.pending = { name, stage, isTextInput: true, inputHint: hints[0] || null }
      } else {
        // Info stage: move on by itself, unless this path has come back round without asking anything
        this.pending = null
        if (this.autoVisited.indexOf(name) !== -1) {
          this.stopReason = `Preview paused: "${name}" leads back round to itself without asking the user anything.`
          this.scrollToBottom()
          return
        }
        this.autoVisited.push(name)
        this.scrollToBottom()
        const runId = this.runId
        this.timer = setTimeout(() => {
          if (runId === this.runId) this.advance(stage)
        }, 700)
        return
      }
      this.scrollToBottom()
    },
    submitTextInput () {
      const value = this.textInputValue.trim()
      if (!value || !this.pending) return
      const stage = this.pending.stage
      this.messages.push({ role: 'user', text: value })
      this.textInputValue = ''
      this.pending = null
      this.autoVisited = []
      this.goTo(this.nextStageFor(stage, 0))
    },
    selectChoice (index) {
      if (!this.pending) return
      const { stage, buttons } = this.pending
      this.messages.push({ role: 'user', text: buttons[index] })
      this.pending = null
      this.autoVisited = []
      this.goTo(this.nextStageFor(stage, index))
    },
    advance (stage) {
      const s = stage || (this.pending && this.pending.stage)
      if (!s) return
      if (!stage) this.pending = null
      this.goTo(this.nextStageFor(s, 0))
    },
    restart () {
      clearTimeout(this.timer)
      // Any step still scheduled from the previous run sees a different runId and does nothing
      this.runId++
      this.messages = []
      this.pending = null
      this.ended = false
      this.endNote = ''
      this.stopReason = ''
      this.stale = false
      this.autoVisited = []
      this.$nextTick(() => this.enterStage('uninitialized'))
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.journey-preview {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}
.jp-empty {
  color: #a0aec0;
  font-size: 13px;
  text-align: center;
  margin-top: 60px;
  line-height: 1.8;
  padding: 0 20px;
}
.jp-lang-bar {
  display: flex;
  gap: 4px;
  padding: 8px 12px 0;
  flex-shrink: 0;
  flex-wrap: wrap;
}
.jp-lang-btn {
  background: none;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  color: #718096;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.jp-lang-btn:hover {
  border-color: #0D9488;
  color: #0D9488;
}
.jp-lang-btn--active {
  background: #0D9488;
  border-color: #0D9488;
  color: #fff;
}
.jp-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.jp-msg {
  display: flex;
}
.jp-msg.user {
  justify-content: flex-end;
}
.jp-msg.bot {
  justify-content: flex-start;
}
.jp-msg-inner {
  display: flex;
  flex-direction: column;
  max-width: 85%;
}
.jp-msg.user .jp-msg-inner {
  align-items: flex-end;
}
.jp-stage-label {
  font-size: 10px;
  color: #b0bec5;
  margin-top: 3px;
  padding: 0 4px;
  font-family: monospace;
}
.jp-bubble {
  padding: 9px 13px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.6;
  background: #edf2f7;
  color: #2d3748;
}
.jp-msg.user .jp-bubble {
  background: #0D9488;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.jp-msg.bot .jp-bubble {
  border-bottom-left-radius: 4px;
}
.jp-hint {
  font-size: 11px;
  color: #a0aec0;
  margin-top: 4px;
  margin-left: 4px;
}
.jp-stopped {
  font-size: 12px;
  color: #8A5A00;
  text-align: center;
  padding: 8px;
  background: #FFF8E6;
  border-radius: 8px;
  border: 1px solid #F5DFA8;
}
.jp-ended {
  font-size: 12px;
  color: #38a169;
  text-align: center;
  padding: 8px;
  background: #f0fff4;
  border-radius: 8px;
  border: 1px solid #c6f6d5;
}
.jp-actions {
  border-top: 1px solid #e2e8f0;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}
.jp-choice-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.jp-choice-btn {
  background: #fff;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #2d3748;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s;
}
.jp-choice-btn:hover {
  background: #E6F5F4;
  border-color: #0D9488;
  color: #0B7C72;
}
.jp-text-input-bar {
  display: flex;
  gap: 6px;
  align-items: center;
}
.jp-text-input {
  flex: 1;
  border: 1px solid #cbd5e0;
  border-radius: 20px;
  padding: 7px 14px;
  font-size: 13px;
  outline: none;
}
.jp-text-input:focus {
  border-color: #0D9488;
}
.jp-text-submit {
  background: #0D9488;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 16px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.jp-text-submit:hover {
  background: #0B7C72;
}
.jp-restart-btn {
  background: none;
  border: none;
  font-size: 12px;
  color: #a0aec0;
  cursor: pointer;
  padding: 2px 0;
  align-self: flex-end;
}
.jp-restart-btn:hover {
  color: #718096;
}
</style>



// WEBPACK FOOTER //
// JourneyPreview.vue?5985a22f